import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Minus } from 'lucide-react'
import { memo } from 'react'

import { useHotelsI18n } from '../../i18n'
import { valueOrPlaceholder } from '../../utils/display'
import { formatPendingTime } from '../../utils/formatting'

export interface PendingChange {
  roomId: number
  roomNumber: string
  roomName: string
  extensionUuid: string
  date: string
  time: string
}

interface PendingChangesTableProps {
  changes: PendingChange[]
  onRemove: (index: number) => void
  onApply: () => void
  onReset: () => void
  isApplying: boolean
  canApplyChanges: boolean
  pbxTimeZone: string
  locale: string
}

export const PendingChangesTable = memo(function PendingChangesTable({
  changes,
  onRemove,
  onApply,
  onReset,
  isApplying,
  canApplyChanges,
  pbxTimeZone,
  locale,
}: PendingChangesTableProps) {
  const { text } = useHotelsI18n()
  return (
    <VStack align="stretch" gap={3}>
      <HStack justify="space-between">
        <Text fontWeight="semibold">
          {text('hotels.wakeupScheduler.pendingChanges', 'Pending Changes')}
        </Text>
        <HStack gap={2}>
          {canApplyChanges && (
            <Button
              size="sm"
              colorPalette="brand"
              borderRadius="l2"
              onClick={onApply}
              disabled={changes.length === 0}
              loading={isApplying}
            >
              {text('hotels.wakeupScheduler.applyChanges', 'Apply Changes')}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            borderRadius="l2"
            onClick={onReset}
            disabled={changes.length === 0 || isApplying}
          >
            {text('hotels.wakeupScheduler.reset', 'Reset')}
          </Button>
        </HStack>
      </HStack>

      <Box
        borderWidth={1}
        borderColor="border.default"
        borderRadius="lg"
        bg="bg.default"
        overflow="hidden"
      >
        <Table.Root size="sm" variant="line">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader px={4} py={2}>
                {text('hotels.wakeupScheduler.roomNumber', 'Room Number')}
              </Table.ColumnHeader>
              <Table.ColumnHeader px={4} py={2}>
                {text('hotels.wakeupScheduler.roomName', 'Room Name')}
              </Table.ColumnHeader>
              <Table.ColumnHeader px={4} py={2}>
                {text(
                  'hotels.wakeupScheduler.pendingChanges',
                  'Pending Changes',
                )}
              </Table.ColumnHeader>
              <Table.ColumnHeader px={4} py={2} w="40px" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {changes.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={4} textAlign="center" py={6}>
                  <Text fontSize="sm" color="fg.muted">
                    {text(
                      'hotels.wakeupScheduler.noPendingChanges',
                      'No pending changes yet',
                    )}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              changes.map((change, index) => {
                const pendingTime = formatPendingTime(
                  change.date,
                  change.time,
                  locale,
                  pbxTimeZone,
                )
                return (
                  <Table.Row
                    key={`${change.roomId}-${change.date}-${change.time}-${index}`}
                  >
                    <Table.Cell px={4} py={2}>
                      <Text fontSize="sm">{change.roomNumber}</Text>
                    </Table.Cell>
                    <Table.Cell px={4} py={2}>
                      <Text fontSize="sm">
                        {valueOrPlaceholder(change.roomName, text)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell px={4} py={2}>
                      <Text fontSize="sm">{pendingTime}</Text>
                    </Table.Cell>
                    <Table.Cell px={4} py={2}>
                      <IconButton
                        aria-label={text(
                          'hotels.wakeupScheduler.removePendingAria',
                          'Remove pending wake-up for room {{room}}, {{time}}',
                          { room: change.roomNumber, time: pendingTime },
                        )}
                        size="2xs"
                        variant="subtle"
                        borderRadius="l2"
                        colorPalette="error"
                        color="fg.error"
                        _disabled={{ color: 'fg.error' }}
                        onClick={() => onRemove(index)}
                        disabled={!canApplyChanges || isApplying}
                      >
                        <Icon as={Minus} boxSize={3} color="fg.error" />
                      </IconButton>
                    </Table.Cell>
                  </Table.Row>
                )
              })
            )}
          </Table.Body>
        </Table.Root>
      </Box>
    </VStack>
  )
})
